"use client";

import {
  addAreaShadeLayer,
  shadeRadiusMeters,
  type AreaShadeBBox,
  type AreaShadeLayer,
  type AreaShadeMap,
} from "@repo/maps";
import { useEffect, useMemo, useRef } from "react";

type AreaPlace = {
  subdivisionId: number;
  center: { lat: number; lng: number };
  bbox: AreaShadeBBox | null;
};

const SOURCE_ID = "search-area-shades";
const NO_AREAS: AreaPlace[] = [];

export function useMapAreaShades(
  map: AreaShadeMap | null,
  areas: AreaPlace[] | undefined,
  viewport: AreaShadeBBox | null,
  hoveredId: number | null,
) {
  const handleRef = useRef<AreaShadeLayer | null>(null);
  const places = areas ?? NO_AREAS;
  const shades = useMemo(
    () =>
      places.flatMap((area) => {
        const radiusMeters = shadeRadiusMeters({
          bbox: area.bbox,
          viewport,
        });
        if (radiusMeters == null) return [];
        return [{ id: area.subdivisionId, center: area.center, radiusMeters }];
      }),
    [places, viewport],
  );
  const shadesRef = useRef(shades);
  const hoveredRef = useRef(hoveredId);
  shadesRef.current = shades;
  hoveredRef.current = hoveredId;

  useEffect(() => {
    if (!map) return;
    const handle = addAreaShadeLayer(map, { sourceId: SOURCE_ID });
    const current = shadesRef.current;
    handle.setAreas(current);
    handle.setHovered(
      current.some((shade) => shade.id === hoveredRef.current)
        ? hoveredRef.current
        : null,
    );
    handleRef.current = handle;
    return () => {
      handle.remove();
      if (handleRef.current === handle) handleRef.current = null;
    };
  }, [map]);

  useEffect(() => {
    handleRef.current?.setAreas(shades);
  }, [shades]);

  useEffect(() => {
    const visible = shades.some((shade) => shade.id === hoveredId);
    handleRef.current?.setHovered(visible ? hoveredId : null);
  }, [hoveredId, shades]);
}
